import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatTime } from "@/lib/format";
import { Radio, Search, X, Phone, AlertTriangle, Info } from "lucide-react";
import { useVisiblePolling } from "@/hooks/useVisiblePolling";
import { Avatar } from "@/components/shared/Avatar";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { TabuleMap } from "@/components/tabule/TabuleMap";
import { OFF, NOGPS, buildPeople, buildPlaces, buildClusters, buildChips, summary, filterPeople, groupList, pinIds } from "@/components/tabule/tabuleData";

// Tabule „kdo kde je“ (337): každý přihlášený na mapě podle místa příchodu — u stavby
// (podle polohy, i na denním výkazu), mimo stavby, nebo bez polohy. Žetony filtrují,
// klik na člověka ho ukáže na mapě, klik na mapě ho najde v seznamu, 📞 zavolá.
const time = (iso) => (iso ? formatTime(iso).replace(/^0(\d)/, "$1") : "—");

function PersonRow({ p, selected, onPick }) {
  const line = p.group === NOGPS
    ? <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400 font-bold"><AlertTriangle size={13} className="shrink-0" />Bez polohy · od {time(p.since)}</span>
    : <span className="block truncate">{p.group === OFF ? "Mimo stavby" : p.placeName} · od {time(p.since)}{p.group === OFF && p.declared ? ` · zapsán: ${p.declared}` : ""}</span>;
  return (
    <div data-pid={p.id} data-testid="liveboard-row"
      className={`flex items-center gap-2.5 rounded-[20px] bg-card pl-3 pr-2 py-2 ${selected ? "border-2 border-foreground pl-[11px] pr-[7px] py-[7px]" : "border border-border"}`}>
      <button type="button" onClick={() => onPick(p)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`tabule-person-${p.id}`}>
        <span className="flex rounded-full p-[2px] shrink-0" style={{ background: p.color }}>
          <Avatar name={p.name} photo={p.photo} size={34} className="bg-transparent text-white" />
        </span>
        <span className="min-w-0 flex-1">
          <b className="block truncate">{p.name}</b>
          <span className="block text-[13px] text-muted-foreground min-w-0">{line}</span>
        </span>
      </button>
      {p.phone && (
        <a href={`tel:${p.phone.replace(/\s+/g, "")}`} title={`Zavolat ${p.phone}`} aria-label={`Zavolat ${p.name}`} data-testid={`tabule-call-${p.id}`}
          className="w-11 h-11 shrink-0 rounded-full border border-border bg-card grid place-items-center hover:bg-success/10 hover:border-success transition-colors">
          <Phone size={18} />
        </a>
      )}
    </div>
  );
}

export default function LiveBoard() {
  const [rows, setRows] = useState([]);
  const [group, setGroup] = useState(null);
  const [q, setQ] = useState("");
  const [selId, setSelId] = useState(null);
  const [info, setInfo] = useState(false);
  const mobile = useIsMobile();
  const listRef = useRef(null);

  const load = useCallback(() => api.get("/attendance/live-board").then((r) => setRows(r.data)).catch(() => {}), []);
  useVisiblePolling(load, 30000);

  const { people, places, clusters } = useMemo(() => {
    const pe = buildPeople(rows);
    const cl = buildClusters(pe);
    return { people: pe, places: buildPlaces(rows, pe), clusters: cl };
  }, [rows]);
  const chips = useMemo(() => buildChips(people, places), [people, places]);
  const sum = useMemo(() => summary(people), [people]);
  const shown = useMemo(() => filterPeople(people, { group, q }), [people, group, q]);
  const visibleIds = useMemo(() => new Set(shown.map((p) => p.id)), [shown]);
  const pins = useMemo(() => pinIds(people, { group, selId, q, visibleIds }), [people, group, selId, q, visibleIds]);
  const groups = useMemo(() => groupList(shown, places), [shown, places]);
  const fitKey = `${selId}|${group}|${q ? [...visibleIds].join(",") : ""}|${people.map((p) => p.id).join(",")}`;

  // Vybraný odešel / skupina už nikoho nemá → výběr se zruší sám.
  useEffect(() => { if (selId && !people.some((p) => p.id === selId)) setSelId(null); }, [people, selId]);
  useEffect(() => {
    if (group && !people.some((p) => p.group === group || p.cluster === group)) setGroup(null);
  }, [people, group]);

  const pickPerson = (p) => { if (p.hasGps) setSelId((cur) => (cur === p.id ? null : p.id)); };
  const pickGroup = (key) => { setGroup((cur) => (cur === key ? null : key)); setSelId(null); setQ(""); };
  const pickPin = (id) => {
    setSelId((cur) => (cur === id ? null : id));
    setTimeout(() => listRef.current?.querySelector(`[data-pid="${id}"]`)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" }), 0);
  };

  const cluster = clusters.find((c) => c.key === group);
  const chipBtn = (c, on) => (
    <button key={c.key || "all"} type="button" onClick={() => pickGroup(c.key)} data-testid={`tabule-chip-${c.key || "all"}`}
      className={`shrink-0 h-9 px-3 rounded-full border text-sm font-bold flex items-center gap-2 whitespace-nowrap transition-colors ${on
        ? (c.warn ? "bg-amber-700 border-amber-700 text-white" : "bg-foreground text-background border-foreground")
        : (c.warn ? "bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/40" : "bg-card border-border hover:bg-secondary")}`}>
      {c.color && !c.warn && <i className="w-2 h-2 rounded-full shrink-0" style={{ background: c.color }} />}
      {c.warn && <AlertTriangle size={14} />}
      {c.label}<span className="tabular-nums opacity-60">{c.count}</span>
    </button>
  );
  const chipRow = (
    <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="tabule-chips">
      {cluster && (
        <button type="button" onClick={() => pickGroup(cluster.key)} data-testid="tabule-chip-cluster"
          className="shrink-0 h-9 px-3 rounded-full border border-foreground bg-foreground text-background text-sm font-bold flex items-center gap-1.5 whitespace-nowrap">
          {cluster.ids.length} lidí na jednom místě <X size={14} />
        </button>
      )}
      {chipBtn({ key: null, label: "Všichni", count: people.length }, !group)}
      {chips.map((c) => chipBtn(c, group === c.key))}
    </div>
  );
  const search = (
    <div className="flex items-center gap-2 h-11 rounded-[16px] border border-border bg-card px-3 shrink-0">
      <Search size={16} className="text-muted-foreground shrink-0" />
      <input value={q} onChange={(e) => { setQ(e.target.value); setSelId(null); }} placeholder="Hledat člověka…" data-testid="tabule-search"
        className="flex-1 min-w-0 bg-transparent outline-none text-sm" />
      {q && <button type="button" onClick={() => setQ("")} aria-label="Smazat hledání" className="w-[22px] h-[22px] rounded-full bg-secondary grid place-items-center text-muted-foreground"><X size={12} /></button>}
    </div>
  );
  const list = (
    <div ref={listRef} className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto space-y-1.5 pb-1" data-testid="tabule-list">
      {groups.length === 0 && (
        <div className="py-6 text-center text-sm text-muted-foreground">Nikdo neodpovídá.
          <div><button type="button" onClick={() => { setQ(""); setGroup(null); setSelId(null); }} className="mt-2 h-10 px-4 rounded-[14px] border border-border bg-card font-bold">Zrušit filtry</button></div>
        </div>
      )}
      {groups.map((g) => (
        <div key={g.key} className="space-y-1.5">
          <div className={`flex items-center gap-2 pt-2.5 pb-0.5 px-1 text-xs font-extrabold uppercase tracking-wider ${g.warn ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>
            {g.warn ? <AlertTriangle size={13} /> : <i className="w-2 h-2 rounded-full" style={{ background: g.color }} />}{g.label} · {g.people.length}
          </div>
          {g.people.map((p) => <PersonRow key={p.id} p={p} selected={selId === p.id} onPick={pickPerson} />)}
        </div>
      ))}
    </div>
  );
  const map = (
    <TabuleMap people={people} places={places} clusters={clusters} pins={pins} group={group} selId={selId}
      searching={!!q} visibleIds={visibleIds} fitKey={fitKey} compact={mobile} onPick={pickPin} onGroup={pickGroup}
      className="h-full w-full" />
  );

  return (
    <div className="space-y-3 md:space-y-4" data-testid="liveboard-page">
      <div>
        <h1 className="text-3xl font-black tracking-tighter uppercase flex items-center gap-3"><Radio className="text-brand" /> Živá tabule</h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="tabule-summary">
          {sum.total} v práci · {sum.onSite} na stavbách · {sum.off} mimo stavby
          {sum.nogps > 0 && <b className="text-amber-700 dark:text-amber-400"> · {sum.nogps} bez polohy</b>}
          <button type="button" onClick={() => setInfo((v) => !v)} aria-label="Co ukazuje mapa" aria-expanded={info} data-testid="tabule-info"
            className="ml-1.5 inline-grid place-items-center w-6 h-6 align-middle rounded-full text-muted-foreground hover:bg-secondary"><Info size={15} /></button>
        </p>
        {info && <p className="text-xs text-muted-foreground mt-1">Poloha = místo, kde se člověk přihlásil do práce. Kdo pak odjel, zůstává na mapě tam.</p>}
      </div>

      {people.length === 0 ? (
        <p className="text-muted-foreground">Nikdo není právě přihlášen.</p>
      ) : mobile ? (
        <>
          {/* mapa zůstává nahoře pod plovoucí lištou i při rolování seznamu */}
          <div className="sticky z-10 -mx-4 px-4 pb-2 pt-1 bg-background/85 backdrop-blur space-y-2"
            style={{ top: "calc(max(14px, calc(env(safe-area-inset-top) + 6px)) + 50px)" }}>
            {chipRow}
            <div className="h-[200px]">{map}</div>
          </div>
          {search}
          {list}
        </>
      ) : (
        <>
          {chipRow}
          {/* tablet: mapa nahoře přes celou šířku, seznam pod ní; od 1024 px vedle sebe */}
          <div className="grid gap-4 lg:grid-cols-[360px_1fr] lg:h-[calc(100vh-250px)] lg:min-h-[460px]">
            <div className="flex flex-col gap-2 min-h-0">{search}{list}</div>
            <div className="order-first lg:order-none h-[380px] lg:h-full min-w-0">{map}</div>
          </div>
        </>
      )}
    </div>
  );
}
